import { type Option } from '@allesauto/core/searchDropdown';
import { SchemaTypes } from '@allesauto/graphql';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  CustomerVehicle,
  DealerDetailsService,
  ServiceBookingProps,
  WorkshopServiceContactForm,
} from '@/global/models/IServiceBooking';
import { RootState } from '@/store/redux';

const SLICE_NAME_SERVICE_BOOKING = 'serviceBooking';

export const initialState: ServiceBookingProps = {
  serviceDetails: {
    sku: '',
    description: null,
    title: '',
    price: null,
    abstractProductSku: null,
  },
  date: null,
  timeslot: null,
  dealerFinderOption: {
    option: null,
    latLng: null,
  },
  vehicle: {
    garageId: null,
    vin: null,
    make: null,
    mileage: null,
    model: null,
    plateNumber: null,
    registeredIn: null,
    fuelType: null,
  },
  dealerDetails: {
    id: '',
    distance: null,
    location: {
      city: '',
      houseNumber: '',
      street: '',
      zip: '',
      additionalAddress: null,
      geoCoordinates: {
        lat: null,
        lon: null,
      },
    },
    name: null,
  },
  contactForm: {
    userMsg: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
  },
  isGarageFlow: false,
};

const slice = createSlice({
  name: SLICE_NAME_SERVICE_BOOKING,
  initialState,
  reducers: {
    setServiceDetails(state, action: PayloadAction<SchemaTypes.Service>) {
      state.serviceDetails = {
        ...action.payload,
        price: null,
      };
    },
    setDealerFinderOption(
      state,
      action: PayloadAction<{
        option: Option | null;
        latLng: SchemaTypes.GeoCoordinates | null;
      }>
    ) {
      state.dealerFinderOption = action.payload;
    },

    setDateTime(
      state,
      action: PayloadAction<{
        date: Date | null;
        timeslot: Option | null;
      }>
    ) {
      state.date = action.payload.date;
      state.timeslot = action.payload.timeslot;
    },

    setVehicleDetails(
      state,
      action: PayloadAction<{
        vehicle: CustomerVehicle;
      }>
    ) {
      state.vehicle = { ...action.payload.vehicle };
    },

    setDealerDetails(state, action: PayloadAction<DealerDetailsService>) {
      state.dealerDetails = { ...action.payload };
    },

    setServicePrice(state, action: PayloadAction<{ price?: number }>) {
      state.serviceDetails.price = action.payload.price;
    },

    setGarageFlow(state, action: PayloadAction<boolean>) {
      state.isGarageFlow = action.payload;
    },

    setContactFormDetails(
      state,
      action: PayloadAction<WorkshopServiceContactForm>
    ) {
      state.contactForm = { ...action.payload };
    },
    handleUpdateServiceBooking(
      state,
      action: PayloadAction<ServiceBookingProps>
    ) {
      return action.payload;
    },

    reset() {
      localStorage.removeItem('serviceBooking');
      return initialState;
    },
  },
});

export const {
  setServiceDetails,
  setDealerFinderOption,
  setDateTime,
  setVehicleDetails,
  setDealerDetails,
  reset,
  setServicePrice,
  setGarageFlow,
  setContactFormDetails,
  handleUpdateServiceBooking,
} = slice.actions;

export const getServiceBookingState = (state: RootState): ServiceBookingProps =>
  state.pages.serviceBooking.page;

export default slice.reducer;
